import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/utils/axios";
import cls from "./ClubMembershipSingle.module.scss";

const ROLE_OPTIONS = [
  { value: "member", label: "Member" },
  { value: "host", label: "Host" },
  { value: "partner", label: "Partner" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "removed", label: "Removed" },
  { value: "banned", label: "Banned" },
];

const toInputDT = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const fromInputDT = (val) => {
  if (!val) return null;
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
};

const ClubMembershipSingle = () => {
  const { cityId, streetId, clubId, membershipId } = useParams();

  const base = useMemo(
    () => `/v1/admin/cities/${cityId}/streets/${streetId}/clubs/${clubId}`,
    [cityId, streetId, clubId]
  );

  const backToClubPath = useMemo(
    () => `/admin/cities/single/${cityId}/streets/${streetId}/clubs/${clubId}`,
    [cityId, streetId, clubId]
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const [data, setData] = useState(null);

  // editable fields
  const [role, setRole] = useState("member");
  const [status, setStatus] = useState("active");
  const [assignedAt, setAssignedAt] = useState("");
  const [joinedAt, setJoinedAt] = useState("");

  const memberProfileLink = useMemo(() => {
    const uid = data?.userId;
    return uid ? `/admin/members/single/${uid}` : "#";
  }, [data?.userId]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErr("");
      setMsg("");

      const { data: res } = await api.get(
        `${base}/memberships/${membershipId}`
      );

      setData(res);
      setRole(res?.membershipRole || "member");
      setStatus(res?.membershipStatus || "active");
      setAssignedAt(toInputDT(res?.assignedAt));
      setJoinedAt(toInputDT(res?.joinedAt));
    } catch (e) {
      setErr(
        e?.response?.data?.message || e?.message || "Failed to load membership."
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [base, membershipId]);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async (e) => {
    e.preventDefault();
    if (!data?.membershipId) return;

    try {
      setSaving(true);
      setErr("");
      setMsg("");

      const payload = {
        role,
        status,
        assignedAt: assignedAt ? fromInputDT(assignedAt) : null,
        joinedAt: joinedAt ? fromInputDT(joinedAt) : null,
      };

      const { data: updated } = await api.patch(
        `${base}/memberships/${membershipId}`,
        payload
      );

      setData(updated);
      setRole(updated?.membershipRole || role);
      setStatus(updated?.membershipStatus || status);
      setAssignedAt(toInputDT(updated?.assignedAt));
      setJoinedAt(toInputDT(updated?.joinedAt));

      setMsg("Membership updated.");
    } catch (e2) {
      setErr(e2?.response?.data?.message || e2?.message || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const avatarUrl = data?.avatar?.url || "";
  const username = data?.username || "unknown";
  const userStatus = data?.userStatus || "—";

  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <div className={cls.headerLeft}>
          <div className={cls.linksRow}>
            <Link to={backToClubPath} className={cls.linkPill}>
              ← Back to Club
            </Link>

            {data?.userId ? (
              <Link to={memberProfileLink} className={cls.linkPillAlt}>
                View Member Profile →
              </Link>
            ) : (
              <span className={`${cls.linkPillAlt} ${cls.linkDisabled}`}>
                View Member Profile →
              </span>
            )}
          </div>

          <p className={cls.eyebrow}>Admin · Club Membership</p>
          <h1 className={cls.title}>Membership Details</h1>
          <p className={cls.sub}>
            View and update this member’s role and status in the club.
          </p>
        </div>

        <div className={cls.headerRight}>
          <button
            type="button"
            className={`nu-btn nu-btn--ghost ${cls.refreshBtn}`}
            onClick={load}
            disabled={loading || saving}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </header>

      {err && <div className={cls.alertErr}>⚠ {err}</div>}
      {msg && <div className={cls.alertOk}>{msg}</div>}

      {loading ? (
        <div className={cls.skeleton}>Loading membership…</div>
      ) : !data ? (
        <div className={cls.empty}>Membership not found.</div>
      ) : (
        <div className={cls.grid}>
          {/* Left: Profile summary */}
          <section className={cls.card}>
            <div className={cls.cardHead}>
              <h2 className={cls.cardTitle}>Member</h2>
              <span className={cls.badge}>{data?.membershipStatus || "—"}</span>
            </div>

            <div className={cls.profileRow}>
              <div className={cls.avatarWrap}>
                {avatarUrl ? (
                  <img className={cls.avatar} src={avatarUrl} alt={username} />
                ) : (
                  <div className={cls.avatarFallback} />
                )}
              </div>

              <div className={cls.profileMain}>
                <div className={cls.username}>@{username}</div>

                <div className={cls.pills}>
                  <span className={cls.pill}>user: {userStatus}</span>
                  <span className={cls.pillMuted}>
                    role: {data?.membershipRole || "—"}
                  </span>
                </div>

                <div className={cls.meta}>
                  <div className={cls.metaRow}>
                    <span className={cls.metaKey}>Membership ID</span>
                    <span className={cls.metaVal}>{data?.membershipId}</span>
                  </div>
                  <div className={cls.metaRow}>
                    <span className={cls.metaKey}>User ID</span>
                    <span className={cls.metaVal}>{data?.userId}</span>
                  </div>
                  <div className={cls.metaRow}>
                    <span className={cls.metaKey}>Member ID</span>
                    <span className={cls.metaVal}>{data?.memberId || "—"}</span>
                  </div>

                  <div className={cls.metaRow}>
                    <span className={cls.metaKey}>Assigned By</span>
                    <span className={cls.metaVal}>
                      {data?.assignedByUser?.username
                        ? `@${data.assignedByUser.username}`
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className={cls.timeGrid}>
              <div className={cls.timeItem}>
                <div className={cls.timeLabel}>Assigned At</div>
                <div className={cls.timeVal}>{fmt(data?.assignedAt)}</div>
              </div>
              <div className={cls.timeItem}>
                <div className={cls.timeLabel}>Joined At</div>
                <div className={cls.timeVal}>{fmt(data?.joinedAt)}</div>
              </div>
            </div>
          </section>

          {/* Right: Update form */}
          <section className={cls.card}>
            <div className={cls.cardHead}>
              <h2 className={cls.cardTitle}>Update</h2>
              <span className={cls.hint}>
                Changes are saved to ClubMembership
              </span>
            </div>

            <form className={cls.form} onSubmit={onSave}>
              <div className={cls.formGrid}>
                <label className={cls.field}>
                  <span className={cls.label}>Role</span>
                  <select
                    className={cls.select}
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    disabled={saving}
                  >
                    {ROLE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className={cls.field}>
                  <span className={cls.label}>Membership Status</span>
                  <select
                    className={cls.select}
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    disabled={saving}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className={cls.field}>
                  <span className={cls.label}>Assigned At</span>
                  <input
                    className={cls.input}
                    type="datetime-local"
                    value={assignedAt}
                    onChange={(e) => setAssignedAt(e.target.value)}
                    disabled={saving}
                  />
                  <span className={cls.help}>Leave empty to set null.</span>
                </label>

                <label className={cls.field}>
                  <span className={cls.label}>Joined At</span>
                  <input
                    className={cls.input}
                    type="datetime-local"
                    value={joinedAt}
                    onChange={(e) => setJoinedAt(e.target.value)}
                    disabled={saving}
                  />
                  <span className={cls.help}>Leave empty to set null.</span>
                </label>
              </div>

              <div className={cls.actions}>
                <button
                  className={`nu-btn nu-btn--green ${cls.saveBtn}`}
                  type="submit"
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save Changes"}
                </button>

                <button
                  type="button"
                  className={`nu-btn nu-btn--ghost ${cls.cancelBtn}`}
                  onClick={load}
                  disabled={saving}
                >
                  Reset
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </div>
  );
};

export default ClubMembershipSingle;
